import { createFileRoute, Link } from '@tanstack/react-router';
import { PAYMENT_FREQUENCY_LABELS } from '@accounting/shared';
import { useShareholderLoans } from '../../hooks/useShareholderLoans';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/shareholder-loans/')({
  component: ShareholderLoansPage,
});

function ShareholderLoansPage() {
  const companyId = useActiveCompanyId();
  const { data: loans = [], isLoading, error } = useShareholderLoans(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Shareholder loans</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Shareholder loans</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Notes the company holds — repayments reduce the receivable; interest is income</p>
        </div>
        <Link to="/shareholder-loans/new" className="btn-primary" style={{ textDecoration: 'none' }}>+ New loan</Link>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load loans.</p>}

      {!isLoading && loans.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No shareholder loans yet.</p>
          <Link to="/shareholder-loans/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>Add your first loan</Link>
        </div>
      )}

      {loans.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Loan</th>
              <th>Next due</th>
              <th style={{ textAlign: 'right' }}>Repayment</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th style={{ textAlign: 'right' }}>Balloon</th>
              <th>Maturity</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {loans.map((l) => (
              <tr key={l.id} style={{ opacity: l.status === 'paid_off' ? 0.6 : 1 }}>
                <td style={{ fontWeight: 500 }}>{l.name}</td>
                <td>{l.nextDueDate ? formatAccountingDate(l.nextDueDate) : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {formatCurrency(l.paymentAmount)} <span className="muted" style={{ fontSize: 12 }}>{PAYMENT_FREQUENCY_LABELS[l.paymentFrequency].toLowerCase()}</span>
                </td>
                <td style={{ textAlign: 'right' }}>{Number(l.annualRatePct)}%</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.currentBalance)}</td>
                <td style={{ textAlign: 'right' }}>{Number(l.projectedBalloon) > 0 ? formatCurrency(l.projectedBalloon) : ''}</td>
                <td>{formatAccountingDate(l.maturityDate)}</td>
                <td>
                  {l.status === 'paid_off' ? <span className="tag">paid off</span> : <span className="tag ok">active</span>}
                  {l.isOverdue && <span className="tag bad" style={{ marginLeft: 4 }}>overdue</span>}
                  {l.autoPostPayments && l.status === 'active' && <span className="tag info" style={{ marginLeft: 4 }}>auto</span>}
                </td>
                <td>
                  <Link to="/shareholder-loans/$id" params={{ id: l.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
